// GlideRingLayer.tsx — Terrain-clipped, wind-corrected emergency glide ring.
//
// Renders a closed polygon outline (solid green, no fill) around ownship
// showing where the aircraft could reach in a power-off glide given:
//   - active aircraft's best-glide TAS + glide ratio
//   - wind aloft near ownship (per-altitude band as the glide descends)
//   - terrain elevation along each ray (clip the ring at first
//     terrain-impact + safety buffer)
//
// Mount: this component has no DOM. It listens to GPS / aircraft /
// winds / terrain settings and pushes a MapLibre GeoJSON source + line
// layer onto the map. Side-effect manager — must mount AFTER MapView.
//
// Layer-picker gating: only computes + renders when
//   useUiStore.layers.glideRing === true.
//
// Recompute triggers (debounced 400ms):
//   - Ownship position changes by > 0.05 NM
//   - Ownship altitude changes by > 50 ft
//   - Wind profile changes (sampled location)
//   - Active aircraft / glide settings change
//   - Emergency-mode flips ON (re-fits ring + highlights airports)

import { useEffect, useRef } from 'react';
import { useGpsStore } from '../stores/gpsStore';
import { useAircraftStore, selectActiveAircraft } from '../stores/aircraftStore';
import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useOwnshipWindsStore } from '../stores/ownshipWindsStore';
import { getMapInstance } from './MapView';
import { computeGlideRing, ringBbox } from '../lib/glideRing';
import { sampleElevationFt } from '../lib/terrainSampler';
import type {
  Map as MlMap,
  GeoJSONSource,
  FillLayerSpecification,
  LineLayerSpecification,
} from 'maplibre-gl';

const SOURCE_ID = 'glide-ring';
const LINE_LAYER_ID = 'glide-ring-line';
const EMERGENCY_FILL_LAYER_ID = 'glide-ring-emergency-fill';

const FT_PER_M = 3.28084;

// Debounce so a stream of GPS fixes doesn't recompute the ring 5x/sec.
const RECOMPUTE_DEBOUNCE_MS = 400;

// Below this AGL the ring collapses to almost nothing; we suppress it
// and surface a 'On ground' status to the user instead. 200 ft is
// roughly traffic-pattern entry altitude over the lowest terrain in
// the contiguous US — anything below that is taxi / takeoff / landing,
// not an emergency-glide situation.
const MIN_AGL_FT_FOR_RING = 200;

interface RingState {
  ring: Array<[number, number]>;
  bbox: [number, number, number, number];
}

function emptyFeatureCollection(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function ringToFeatureCollection(state: RingState | null): GeoJSON.FeatureCollection {
  if (!state || state.ring.length < 3) return emptyFeatureCollection();
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [state.ring] },
        properties: {},
      },
    ],
  };
}

function ensureMapLayers(map: MlMap): void {
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: emptyFeatureCollection(),
    });
  }
  if (!map.getLayer(EMERGENCY_FILL_LAYER_ID)) {
    const fillSpec: FillLayerSpecification = {
      id: EMERGENCY_FILL_LAYER_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        // Only visible in emergency mode (see paint update in effect).
        'fill-color': '#22c55e', // green-500
        'fill-opacity': 0,
      },
    };
    map.addLayer(fillSpec);
  }
  if (!map.getLayer(LINE_LAYER_ID)) {
    const lineSpec: LineLayerSpecification = {
      id: LINE_LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': '#22c55e', // green-500
        'line-width': 3.5,
        'line-opacity': 0.95,
      },
    };
    map.addLayer(lineSpec);
  }
}

function removeMapLayers(map: MlMap): void {
  if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID);
  if (map.getLayer(EMERGENCY_FILL_LAYER_ID)) map.removeLayer(EMERGENCY_FILL_LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

export function GlideRingLayer() {
  const visible = useLayerVisibilityStore((s) => s.layers.glideRing);
  const emergencyMode = useUiStore((s) => s.glideRingEmergencyMode);
  const safetyBufferFt = useUiStore((s) => s.glideRingSafetyBufferFt);

  const gpsFix = useGpsStore((s) => s.fix);
  const aircraft = useAircraftStore(selectActiveAircraft);
  const windsProfile = useOwnshipWindsStore((s) => s.profile);
  const maybeRefreshWinds = useOwnshipWindsStore((s) => s.maybeRefresh);

  // Last-rendered ring snapshot — for emergency-mode bounds fitting.
  const lastRingRef = useRef<RingState | null>(null);
  const setOnGround = useUiStore((s) => s.setGlideRingOnGround);
  // Last-computed position/altitude — for debouncing.
  const lastComputedAtRef = useRef<{ lat: number; lon: number; altFt: number } | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightAc = useRef<AbortController | null>(null);

  // ── Mount/unmount layer scaffolding ─────────────────────────────────
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    if (!visible) {
      removeMapLayers(map);
      lastRingRef.current = null;
      setOnGround(false);
      return;
    }
    // MapLibre may not have finished `load` yet; wait for it.
    if (map.isStyleLoaded()) {
      ensureMapLayers(map);
    } else {
      const handler = () => {
        ensureMapLayers(map);
        map.off('load', handler);
      };
      map.on('load', handler);
    }
    return () => {
      if (!visible) {
        removeMapLayers(map);
      }
    };
  }, [visible]);

  // ── Trigger winds refresh whenever we have a position ──────────────
  useEffect(() => {
    if (!visible || !gpsFix) return;
    maybeRefreshWinds(gpsFix.lat, gpsFix.lon);
  }, [visible, gpsFix?.lat, gpsFix?.lon, maybeRefreshWinds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Recompute ring on relevant input changes (debounced) ───────────
  useEffect(() => {
    if (!visible) return;
    if (!gpsFix || gpsFix.altMeters == null) return;
    if (!aircraft?.glide?.bestGlideKt || !aircraft?.glide?.bestGlideRatio) return;

    const altFt = gpsFix.altMeters * FT_PER_M;

    // Movement gate — skip recompute if we haven't moved enough.
    const last = lastComputedAtRef.current;
    if (last) {
      const dLat = (gpsFix.lat - last.lat) * 60; // 1°lat ≈ 60 NM
      const dLon = (gpsFix.lon - last.lon) * 60 * Math.cos((gpsFix.lat * Math.PI) / 180);
      const distNm = Math.sqrt(dLat * dLat + dLon * dLon);
      const dAltFt = Math.abs(altFt - last.altFt);
      if (distNm < 0.05 && dAltFt < 50 && !emergencyMode) return;
    }

    // Debounce: schedule the actual compute.
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      // Cancel any prior in-flight compute.
      inFlightAc.current?.abort();
      const ac = new AbortController();
      inFlightAc.current = ac;

      const sampler = (lat: number, lon: number) => sampleElevationFt(lat, lon, undefined, ac.signal);

      // AGL gate — if we're on or near the ground, surface 'on ground'
      // state and skip the full compute (the ring would collapse to
      // ~1 step anyway).
      try {
        const groundElevFt = await sampler(gpsFix.lat, gpsFix.lon);
        if (ac.signal.aborted) return;
        if (Number.isFinite(groundElevFt) && altFt - groundElevFt < MIN_AGL_FT_FOR_RING) {
          setOnGround(true);
          const map = getMapInstance();
          if (map) {
            const src = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
            if (src) src.setData(emptyFeatureCollection());
          }
          lastRingRef.current = null;
          lastComputedAtRef.current = { lat: gpsFix.lat, lon: gpsFix.lon, altFt };
          return;
        }
      } catch {
        // Ignore terrain sampler failure here; the main compute below
        // will degrade gracefully on its own.
      }
      setOnGround(false);

      try {
        const result = await computeGlideRing({
          lat: gpsFix.lat,
          lon: gpsFix.lon,
          altMslFt: altFt,
          bestGlideKt: aircraft.glide.bestGlideKt!,
          bestGlideRatio: aircraft.glide.bestGlideRatio!,
          safetyBufferFt,
          sampleTerrainFt: sampler,
          windsProfile,
        });
        if (ac.signal.aborted) return;
        const map = getMapInstance();
        if (!map) return;
        const state: RingState = {
          ring: result.ring,
          bbox: ringBbox(result.ring),
        };
        lastRingRef.current = state;
        lastComputedAtRef.current = { lat: gpsFix.lat, lon: gpsFix.lon, altFt };
        const src = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
        if (src) {
          src.setData(ringToFeatureCollection(state));
        }
      } catch (err: unknown) {
        if ((err as { name?: string }).name === 'AbortError') return;
        console.warn('[GlideRingLayer] compute failed', err);
      }
    }, RECOMPUTE_DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [
    visible,
    gpsFix?.lat,
    gpsFix?.lon,
    gpsFix?.altMeters,
    aircraft?.glide?.bestGlideKt,
    aircraft?.glide?.bestGlideRatio,
    safetyBufferFt,
    windsProfile,
    emergencyMode,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Emergency-mode visual treatment + auto-zoom ────────────────────
  useEffect(() => {
    const map = getMapInstance();
    if (!map || !visible) return;
    if (!map.getLayer(LINE_LAYER_ID)) return;
    if (emergencyMode) {
      // Brighter outline + faint fill for emphasis.
      map.setPaintProperty(LINE_LAYER_ID, 'line-color', '#16a34a'); // green-600
      map.setPaintProperty(LINE_LAYER_ID, 'line-width', 4);
      if (map.getLayer(EMERGENCY_FILL_LAYER_ID)) {
        map.setPaintProperty(EMERGENCY_FILL_LAYER_ID, 'fill-opacity', 0.12);
      }
      // Zoom to ring extent.
      const state = lastRingRef.current;
      if (state) {
        const [w, s, e, n] = state.bbox;
        map.fitBounds(
          [[w, s], [e, n]],
          { padding: 80, duration: 600, maxZoom: 11 },
        );
      }
    } else {
      map.setPaintProperty(LINE_LAYER_ID, 'line-color', '#22c55e');
      map.setPaintProperty(LINE_LAYER_ID, 'line-width', 2.5);
      if (map.getLayer(EMERGENCY_FILL_LAYER_ID)) {
        map.setPaintProperty(EMERGENCY_FILL_LAYER_ID, 'fill-opacity', 0);
      }
    }
  }, [visible, emergencyMode]);

  // ── Cleanup on unmount ──────────────────────────────────────────────
  useEffect(() => {
    return () => {
      inFlightAc.current?.abort();
      const map = getMapInstance();
      if (map) removeMapLayers(map);
    };
  }, []);

  return null;
}
